import Vector from "victor";
import QuillDelta from "quill-delta";
import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeType } from "../types/ShapeType";
import { SimpleShape2 } from "./StandardShapes";
import {
  SimplePointerEventHandler,
  SimplePointerEventHandlerDelegate,
} from "../SimplePointerEventHandler";
import AddStandardShapeOperation from "./AddStandardShapeOperation";
import ThrottledUpsertController from "../toolHelpers/ThrottledUpsertController";
import AnimationFrameMoveController from "../toolHelpers/AnimationFrameMoveController";
import { NodeShape } from "./NodeShape";
import tinycolor from "tinycolor2";
import { Scope } from "../types/Scope";
import { WBHUDState } from "@/store/WBHUDState";
import { HasImageURL } from "../types/attributes";

export class StickyTool implements Tool, SimplePointerEventHandlerDelegate {
  // Populated by bind()
  wb?: WhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;
  throttledUpsertController: ThrottledUpsertController;
  animationFrameMoveController: AnimationFrameMoveController;
  toolType = ToolType.Sticky;

  // New shapes are copied from this object
  shapePrototype: NodeShape = {
    shapeType: ShapeType.Node,
    strokeColor: "#ffff00",
    strokeWidth: 20,
    fillColor: "rgba(0, 0, 0, 0)",
    pointA: [0, 0],
    pointB: [0, 0],
    text: new QuillDelta(),

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };

  // Subclass may change this value
  defaultSize = new Vector(300, 300);

  // Temporary state during an interaction
  isPointerDown = false;

  // Shape info
  shapeID = "";
  shape: SimpleShape2;

  constructor() {
    this.throttledUpsertController = new ThrottledUpsertController(
      (scope: Scope) => {
        if (!this.wb) return;

        this.wb.shapeStore.sendUpsertMany(scope, this.shapeID, this.shape);
      }
    );

    this.animationFrameMoveController = new AnimationFrameMoveController(
      this.onAnimationFrame
    );

    this.eventHandler = new SimplePointerEventHandler();
    // appease typescript
    this.shape = Object.assign({}, this.shapePrototype);
  }

  // Settings passed down from
  // Vuex -> Whiteboard.vue -> WhiteboardController -> this method
  settingsDidChange(settings: WBHUDState) {
    this.shapePrototype.strokeColor = "rgba(0, 0, 0, 0.0)";
    this.shapePrototype.strokeWidth = 0;
    this.shapePrototype.fillColor = settings.fillColorForSticky;
    console.log(this.shapePrototype.fillColor);
    if (tinycolor(this.shapePrototype.fillColor).getAlpha() < 1) {
      // ignore empty fills
      this.shapePrototype.fillColor = "rgba(255, 247, 204, 1.0)";
    }
  }

  nextZIndexDidChange(nextZIndex: number) {
    this.shapePrototype.zIndex = nextZIndex;
  }

  get defaultSizeInWorldSpace(): Vector {
    if (!this.wb) return this.defaultSize.clone();
    return this.defaultSize.clone().multiplyScalar(this.wb.camera.scale);
  }

  onPointerDown = (point: Vector) => {
    if (!this.wb) return;

    const worldPoint = this.wb.camera.convertScreenToWorld(point);
    const stickiesUnderCursor = this.wb.drawing.orderedShapes.filter(
      (shapeDR) => {
        return (
          shapeDR.maySelect &&
          shapeDR.hitTest(worldPoint, true) &&
          shapeDR.shape.shapeType === ShapeType.Node &&
          (shapeDR.shape as HasImageURL).imageURL === undefined
        );
      }
    );
    stickiesUnderCursor.reverse();
    if (stickiesUnderCursor.length) {
      this.wb.selectStickyAndBeginEditing(stickiesUnderCursor[0].id);
      return;
    }

    this.isPointerDown = true;
    this.animationFrameMoveController.onPointerDown(point);

    // Set up pen shape to be used while user is actively drawing
    this.shapeID = this.wb.makeID();
    const origin = this.wb.camera.convertScreenToWorld(new Vector(0, 0));
    const pointA = point
      .clone()
      .subtract(this.defaultSizeInWorldSpace.clone().divideScalar(2));
    const pointB = point.clone().add(this.defaultSizeInWorldSpace);

    this.shape = Object.assign({}, this.shapePrototype, {
      pointA: pointA.toArray(),
      pointB: pointB.toArray(),
      boundingBoxOrigin: origin.toArray(),
      boundingBoxSize: this.wb.camera
        .convertScreenToWorld(this.wb.camera.viewportSize)
        .subtract(origin)
        .toArray(),
      boundingBoxOriginalSize: this.wb.camera.viewportSize.toArray(),
    });

    // Dispatch event to make things show up
    this.throttledUpsertController.call();
  };

  onPointerMove = (point: Vector) => {
    this.animationFrameMoveController.onPointerMove(point);
  };

  onAnimationFrame = (point: Vector) => {
    if (!this.wb) return;
    if (!this.isPointerDown) return;

    // Ignore this frame if mouse hasn't moved
    if (Vector.fromArray(this.shape.pointB).isEqualTo(point)) return;

    const pointA = point
      .clone()
      .subtract(this.defaultSizeInWorldSpace.divideScalar(2));
    this.shape.pointA = pointA.toArray() as [number, number];
    this.shape.pointB = pointA
      .clone()
      .add(this.defaultSizeInWorldSpace)
      .toArray() as [number, number];

    this.throttledUpsertController.call();
  };

  onPointerUp = () => {
    this.throttledUpsertController.cancel();

    if (!this.wb) return;
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.animationFrameMoveController.onPointerUp();

    // Dispatch event to create the new shape

    const center = this.wb.camera.convertScreenToWorld(
      Vector.fromArray(this.shape.pointA)
        .add(Vector.fromArray(this.shape.pointB))
        .divideScalar(2)
    );
    const halfSize = this.defaultSize.clone().divideScalar(2);
    this.shape.pointA = center.clone().subtract(halfSize).toArray() as [
      number,
      number
    ];
    this.shape.pointB = center.clone().add(halfSize).toArray() as [
      number,
      number
    ];

    const newShapeID = this.wb.makeID();
    const op = new AddStandardShapeOperation(
      newShapeID,
      this.wb.camera,
      this.shape,
      true
    );
    this.wb.applyOperation(op);

    // Dispatch events to delete the live shape
    this.wb.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.didCreateShape(this.wb, newShapeID);

    this.shapeID = "";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  didCreateShape(wb: WhiteboardControllerInterface, shapeID: string) {
    wb.selectStickyAndBeginEditing(wb.drawing.indexedShapes[shapeID].id);
  }

  abort() {
    this.isPointerDown = false;

    this.animationFrameMoveController.abort();
    this.throttledUpsertController.cancel();

    this.wb?.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.shapeID = "";
  }

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb;
    this.eventHandler.bind(wb, this);
  }

  unbind() {
    this.wb = undefined;
    this.eventHandler.unbind();
  }
}
